import { App } from 'App'

import { FC, useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { toast } from 'react-toastify'
import { VerificationPage } from 'ufinet-web-components'
import {
	AuthContext,
	authService,
	AuthStatus,
	IAuthUser,
	ITokenData,
	AuthorityPattern,
	UserTypesEnum,
} from 'ufinet-web-functions'
import { PrivateRoutes } from './protected/PrivateRoutes'

const extractTokenFromUrl = (): ITokenData | null => {
	const urlParams = new URLSearchParams(window.location.search)
	const accessToken = urlParams.get('token')
	const refreshToken = urlParams.get('refreshToken')
	if (accessToken) {
		return { accessToken, refreshToken: refreshToken || '' }
	}
	return null
}

const cleanTokenFromUrl = () => {
	const url = new URL(window.location.href)
	url.searchParams.delete('token')
	url.searchParams.delete('refreshToken')
	window.history.replaceState({}, '', url.toString())
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
	try {
		const base64Url = token.split('.')[1]
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join('')
		)
		return JSON.parse(jsonPayload)
	} catch {
		return null
	}
}

const buildUserDataFromJwt = (accessToken: string): IAuthUser | null => {
	const payload = decodeJwtPayload(accessToken)
	if (!payload) return null

	const authorities: AuthorityPattern[] =
		typeof payload.roles === 'string' ? (payload.roles.split(',') as AuthorityPattern[]) : []

	return {
		valid: true,
		expired: false,
		username: (payload.sub as string) || '',
		authorities,
		user: {
			fullName: (payload.fullname as string) || '',
			type: ((payload.usertype as string) || '') as UserTypesEnum,
		},
	}
}

const PORTAL_AUTHORITIES_ENDPOINT = 'portalbackend/api/authorities'

// Returns AuthorityPattern[] on success, [] on non-auth failure (use JWT fallback), null on 401 (must logout)
const fetchAuthorities = async (accessToken: string): Promise<AuthorityPattern[] | null> => {
	const portalApiUrl = process.env.REACT_APP_PORTAL_API_URL
	const portalApiKey = process.env.REACT_APP_PORTAL_API_KEY

	if (!portalApiUrl || !portalApiKey) {
		console.warn('Portal API URL or API Key not configured, falling back to JWT roles')
		return []
	}

	const controller = new AbortController()
	const timeoutId = setTimeout(() => controller.abort(), 5000)

	try {
		const response = await fetch(`${portalApiUrl}${PORTAL_AUTHORITIES_ENDPOINT}`, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				apiKey: portalApiKey,
			},
			signal: controller.signal,
		})

		clearTimeout(timeoutId)

		if (response.status === 401) {
			console.warn('Authorities fetch returned 401, token is invalid')
			return null
		}

		if (!response.ok) {
			console.warn(`Authorities fetch failed with status ${response.status}, falling back to JWT roles`)
			return []
		}

		const data: { authorities?: string[] } = await response.json()

		if (!data.authorities || !Array.isArray(data.authorities) || data.authorities.length === 0) {
			console.warn('Authorities response empty or invalid, falling back to JWT roles')
			return []
		}

		return data.authorities as AuthorityPattern[]
	} catch (error) {
		clearTimeout(timeoutId)
		console.warn('Authorities fetch error, falling back to JWT roles:', error)
		return []
	}
}

const redirectToPortalLogin = () => {
	const portalLoginUrl = process.env.REACT_APP_URL_PORTAL_LOGIN
	if (!portalLoginUrl) {
		console.error('REACT_APP_URL_PORTAL_LOGIN is not configured')
		return
	}
	const currentUrl = window.location.href
	window.location.href = `${portalLoginUrl}?fromLegacyUrl=${encodeURIComponent(currentUrl)}`
}

const AppRoutes: FC = () => {
	const intl = useIntl()
	const { status, setStatus, token, setToken, clear } = useContext(AuthContext)
	const hasInitialized = useRef(false)

	// On mount: extract token from URL or use existing, decode JWT, and log in
	useEffect(() => {
		if (hasInitialized.current) return
		hasInitialized.current = true

		authService.setUpBroadcastChannel({ onLogoutReceived: clear })

		const urlToken = extractTokenFromUrl()
		if (urlToken) {
			cleanTokenFromUrl()
			setToken(urlToken)
		}

		const tokenToUse = urlToken || token
		if (!tokenToUse?.accessToken) {
			redirectToPortalLogin()
			return
		}

		const userData = buildUserDataFromJwt(tokenToUse.accessToken)
		if (!userData) {
			redirectToPortalLogin()
			return
		}

		// Fetch authorities from portal API, falling back to JWT roles
		const isFreshLogin = !!urlToken
		const initializeAuth = async () => {
			const apiAuthorities = await fetchAuthorities(tokenToUse.accessToken)
			if (apiAuthorities === null && isFreshLogin) {
				redirectToPortalLogin()
				return
			}
			if (apiAuthorities && apiAuthorities.length > 0) {
				userData.authorities = apiAuthorities
			}
			setStatus(AuthStatus.LOGGED_IN, userData)
		}

		initializeAuth()
	}, [])

	// Handle logout triggered by FetchAPI 401/403 or BroadcastChannel
	useEffect(() => {
		if (hasInitialized.current && status === AuthStatus.LOGGED_OUT) {
			redirectToPortalLogin()
		}
	}, [status])

	// Periodically refresh token
	const refreshToken = useCallback(async () => {
		if (status !== AuthStatus.LOGGED_IN) return

		const portalApiUrl = process.env.REACT_APP_PORTAL_API_URL
		const portalApiKey = process.env.REACT_APP_PORTAL_API_KEY
		if (!portalApiUrl || !portalApiKey) return

		const authRaw = localStorage.getItem('ufinet-auth')
		if (!authRaw) return

		try {
			const auth = JSON.parse(authRaw)
			if (!auth?.token?.accessToken || !auth?.token?.refreshToken) return

			const response = await fetch(`${portalApiUrl}portalbackend/api/auth/token/refresh`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${auth.token.accessToken}`,
					ApiKey: portalApiKey,
				},
				body: JSON.stringify({ refreshToken: auth.token.refreshToken }),
			})

			if (!response.ok) throw new Error(`Refresh failed with status ${response.status}`)

			const data: { accessToken?: string } = await response.json()
			if (!data.accessToken) throw new Error('No accessToken in refresh response')

			// Update only accessToken in localStorage, keep everything else
			auth.token.accessToken = data.accessToken
			localStorage.setItem('ufinet-auth', JSON.stringify(auth))

			// Update React state
			setToken({ ...auth.token })
		} catch {
			toast.dismiss()
			toast.error(intl.formatMessage({ id: 'AUTH.SESSION.EXPIRED' }))
			redirectToPortalLogin()
		}
	}, [status, setToken, intl])

	useEffect(() => {
		const refreshInterval = setInterval(refreshToken, 15 * 60 * 1000)

		return () => {
			clearInterval(refreshInterval)
		}
	}, [refreshToken])

	return (
		<BrowserRouter>
			<Routes>
				<Route element={<App />}>
					{status === AuthStatus.LOGGED_IN ? (
						<>
							<Route path="/*" element={<PrivateRoutes />} />
						</>
					) : (
						<>
							<Route path="/*" element={<VerificationPage />} />
						</>
					)}
				</Route>
			</Routes>
		</BrowserRouter>
	)
}

export { AppRoutes }
